import { Link } from "react-router-dom";
import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";
import ScrollProgress from "@/components/site/ScrollProgress";
import MobileStickyCTA from "@/components/site/MobileStickyCTA";
import { FadeUp } from "@/components/site/_shared";
import { UpdateCard } from "@/components/site/_partials/UpdateCard";
import { FIELD_UPDATES, EDUCATION_UPDATES, INSTAGRAM_PROFILE } from "@/data/updates";
import useDocumentMeta from "@/hooks/useDocumentMeta";
import { OG_IMAGES, SITE_URL, breadcrumbSchema, organizationSchema } from "@/lib/seo";

export default function Updates() {
  useDocumentMeta({
    title: "Updates, Appearances & Education | Integrated Wellness",
    description: "Field updates, speaking appearances, interviews and physician-reviewed health education from Integrated Wellness and Professor John Camp.",
    image: OG_IMAGES.home,
    url: `${SITE_URL}/updates`,
    schema: [
      organizationSchema(),
      breadcrumbSchema([{ name: "Home", url: "/" }, { name: "Updates", url: "/updates" }]),
    ],
  });

  return (
    <div data-testid="updates-page" className="min-h-screen bg-background">
      <ScrollProgress />
      <Header />
      <main className="pt-28 md:pt-32 pb-24 md:pb-32">
        <section className="max-w-6xl mx-auto px-4 md:px-8">
          <FadeUp className="max-w-4xl">
            <div className="iw-overline">Updates &amp; Education</div>
            <h1 className="mt-4 font-display font-bold tracking-normal leading-[1.04] text-[clamp(2.2rem,5vw,4.2rem)] text-primary">
              In the field. <span className="text-accent">On the record.</span>
            </h1>
            <a href={INSTAGRAM_PROFILE.url} target="_blank" rel="noopener noreferrer" data-testid="updates-page-instagram-profile" className="mt-7 inline-flex font-mono text-xs uppercase tracking-[0.14em] text-primary hover:text-accent transition-colors">
              Follow {INSTAGRAM_PROFILE.handle} →
            </a>
          </FadeUp>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
            {FIELD_UPDATES.map((update, index) => (
              <FadeUp key={update.id} delay={index * 0.08}>
                <UpdateCard update={update} />
              </FadeUp>
            ))}
          </div>
        </section>

        <section className="max-w-6xl mx-auto px-4 md:px-8 mt-24 md:mt-32">
          <FadeUp className="flex items-end justify-between gap-5 border-b-2 border-primary pb-6">
            <div>
              <div className="iw-overline">Education Feed</div>
              <h2 className="mt-3 font-display text-3xl md:text-4xl font-bold text-primary">Physician-reviewed reads.</h2>
            </div>
            <Link to="/evidence" data-testid="updates-evidence-library-link" className="hidden sm:inline-flex font-mono text-xs uppercase tracking-[0.14em] text-accent hover:text-primary transition-colors">Evidence library →</Link>
          </FadeUp>

          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {EDUCATION_UPDATES.map((item, index) => (
              <FadeUp key={item.id} delay={index * 0.06}>
                <Link to={item.href} data-testid={`education-update-${item.id}`} className="group block border border-border bg-card">
                  <div className="aspect-[4/3] overflow-hidden bg-foreground">
                    <img src={item.image} alt={item.imageAlt} data-testid={`education-update-image-${item.id}`} className="h-full w-full object-cover object-center transition-transform duration-500 group-hover:scale-[1.03]" loading="lazy" />
                  </div>
                  <div className="p-5">
                    <div className="font-mono text-xs uppercase tracking-[0.12em] text-accent">{item.category}</div>
                    <h3 className="mt-2 font-display text-lg font-bold text-primary leading-snug">{item.title}</h3>
                  </div>
                </Link>
              </FadeUp>
            ))}
          </div>
        </section>
      </main>
      <Footer />
      <MobileStickyCTA />
    </div>
  );
}